import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const chainFlow = [
  { label: '课堂场景', sub: '沙漠公路', icon: 'ri-landscape-line' },
  { label: '问题', sub: '"为什么要投入"', icon: 'ri-question-answer-line' },
  { label: '民生投入', icon: 'ri-funds-line' },
  { label: '共同富裕', icon: 'ri-scales-line' },
  { label: '以人民为中心', icon: 'ri-heart-line' },
  { label: '制度优势', icon: 'ri-shield-star-line' },
  { label: '指标映射', icon: 'ri-map-pin-2-line' },
  { label: '证据卡 + 可迁移方法', icon: 'ri-file-text-line' },
];

export default function P40CaseDesertRoad({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100/60 text-accent-700 text-[11px] font-body font-semibold">
              <i className="ri-file-text-line text-xs" /> 完整闭环案例 A
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            从<span className="text-primary-600">"沙漠公路"</span>到教学逻辑图谱
          </h2>
        </div>

        {/* 完整链路 */}
        <div className="max-w-4xl mx-auto mb-5 transition-all duration-1000 delay-200">
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2">
            {chainFlow.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-center transition-all duration-300 hover:scale-105 ${i % 2 === 0 ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'}`}>
                  <i className={`${step.icon} text-sm ${i % 2 === 0 ? 'text-primary-500' : 'text-accent-500'}`} />
                  <div>
                    <p className="text-[11px] font-body font-bold text-foreground-800 whitespace-nowrap">{step.label}</p>
                    {step.sub && <p className="text-[10px] text-foreground-400">{step.sub}</p>}
                  </div>
                </div>
                {i < chainFlow.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-300 mx-0.5 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 核心说明 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-5 transition-all duration-1000 delay-400">
          <div className="p-4 rounded-2xl glass-liquid border-primary-200/30">
            <div className="relative z-10">
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-2 flex items-center gap-2">
                <i className="ri-eye-line text-primary-500" /> 系统识别了什么
              </h3>
              <p className="text-xs text-foreground-500 font-body leading-relaxed">
                现实冲突形成问题意识，沙漠公路承担案例功能，民生和共同富裕进入理论阐释，以人民为中心和制度优势完成价值回扣。系统识别的不是几个孤立关键词，而是案例、问题、理论和价值之间的<strong className="text-primary-700">推进关系</strong>。
              </p>
            </div>
          </div>
          <div className="p-4 rounded-2xl glass-liquid-gold border-accent-200/30">
            <div className="relative z-10">
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-500" /> 可迁移方法
              </h3>
              <p className="text-xs text-foreground-500 font-body leading-relaxed">
                "用现实冲突引出理论问题，再通过理论解释完成价值升华"。教学逻辑图谱把课堂片段概括为<strong className="text-accent-700">可进入案例库的方法模板</strong>，青年教师看到的不仅是"这段很好"，而是可以学习案例怎样提出问题、问题怎样进入理论。
              </p>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="max-w-2xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass border-white/50">
            <i className="ri-loop-left-line text-primary-500 text-sm" />
            <span className="text-xs font-body text-foreground-500">
              技术过程变成可理解的教学过程：看见→判断→服务教研
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}